import React, {useState} from "react";
import {Box, Button, Input, Stack} from "@mui/material";
import DefaultDialog, {DefaultDialogSize} from "../../../components/Dialog/ClosableDialog";
import {LabeledSection} from "../../../components/Layout/Page";
import {Organization} from "../../../app/api/model/organization";
import OrganizationSelector from "../../../components/Selectors/OrganizationSelector";
import {useTranslation} from "react-i18next";
import {DeviceField, DeviceFlat} from "../../../app/api/model/device";
import {useOrganizationDeviceAssignMutation} from "../../../app/api/api";
import {useSnackbar} from "notistack";
import CompactDeviceList from "./CompactDeviceList";

interface DeviceAssignToOrganizationDialogProps {
    open: boolean;
    onClose: () => void;
    devices?: DeviceFlat[];
}

export function unassignedOrSameOrganization(devices?: DeviceFlat[]) {
    const ids = devices?.map((d) => d[DeviceField.ORGANIZATION_ID]) ?? [];
    const filteredIDs = ids.filter((id): id is number => id !== undefined && id !== null);
    const organizationIDs = new Set<number>(filteredIDs);
    return organizationIDs.size <= 1;
}

export default function DeviceAssignToOrganizationDialog({
    open,
    onClose,
    devices,
}: DeviceAssignToOrganizationDialogProps) {
    const {t} = useTranslation();
    const [organization, setOrganization] = useState<Organization | undefined>();
    const [comment, setComment] = useState<string>();
    const [assignDevice] = useOrganizationDeviceAssignMutation();
    const {enqueueSnackbar} = useSnackbar();
    const valid = unassignedOrSameOrganization(devices);

    const handleClose = () => {
        setOrganization(undefined);
        onClose?.();
    };

    const onConfirm = async () => {
        if (organization && devices?.length && valid) {
            assignDevice({
                organizationId: organization?.id,
                deviceIds: devices.map((d) => d.id),
                comment: comment,
            })
                .unwrap()
                .then(() => {
                    enqueueSnackbar(t("generic_assignmentSuccessful"), {variant: "success"});
                    handleClose();
                });
        }
    };

    return (
        <DefaultDialog
            size={DefaultDialogSize.EXTRA_LARGE}
            open={open}
            titleKey={"action_assignDeviceToOrg"}
            onClose={handleClose}
            actions={[
                <Box key={"assignDeviceToOrgButton"}>
                    <Button
                        color="primary"
                        variant="contained"
                        type="submit"
                        onClick={onConfirm}
                        disabled={!organization || !valid}
                    >
                        {t("generic_assign")}
                    </Button>
                </Box>,
            ]}
        >
            <Stack spacing={2} pt={1}>
                <LabeledSection title={t("generic_organization")}>
                    <OrganizationSelector value={organization} onChange={setOrganization} columns={2} />
                </LabeledSection>
                <LabeledSection title={t("deviceHistory_comment")} isGridItem={false}>
                    <Input
                        sx={{
                            width: "100%",
                        }}
                        onChange={(event) => {
                            setComment(event.target.value);
                        }}
                    />
                </LabeledSection>
                <LabeledSection title={t("generic_devices")}>
                    <CompactDeviceList devices={devices} />
                </LabeledSection>
            </Stack>
        </DefaultDialog>
    );
}
